import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { inject, InjectionToken } from '@angular/core';
import { ENVIRONMENT_PROVIDER } from '@mkp/shared/util-environment';
import { map, Observable } from 'rxjs';
import { mapAuditDates } from './core-api/revision.mapper';
import { QueryHeaders, QueryParams } from './query-params.model';
import { Revision } from '@mkp/shared/models';

const API_URL = new InjectionToken('API Base Url', {
  factory: () => inject(ENVIRONMENT_PROVIDER).api.core,
});

export interface GetWithQueryOptions {
  params: Partial<QueryParams>;
  headers?: QueryHeaders;
  context?: HttpContext;
  withAccountId?: string;
}

export const QUERY_LIMIT_DEFAULT = 10;

export abstract class BaseHttpResource<Type, Envelop, Post = Type, Patch = Post> {
  protected readonly http = inject(HttpClient);
  protected readonly apiUrl = inject(API_URL);

  protected get url(): string {
    return `${this.apiUrl}/${this.uri}`;
  }

  protected constructor(protected readonly uri: string) {}

  /**
   * Maps a raw DTO from the API to the domain model.
   * Defaults to applying {@link mapAuditDates} when the DTO has audit fields,
   * passing through unchanged otherwise.
   * Override in subclasses to add extra transformations (e.g. email decode).
   */
  protected mapDto(dto: Type): Type {
    if (isRevision(dto)) {
      return { ...dto, ...mapAuditDates(dto) };
    }
    return dto;
  }

  add(item: Post, context?: HttpContext): Observable<Type> {
    return this.http.post<Type>(this.url, item, { context }).pipe(map((dto) => this.mapDto(dto)));
  }

  delete(id: string | number, context?: HttpContext): Observable<undefined> {
    return this.http.delete<undefined>(`${this.url}/${id}`, { context });
  }

  getAll(context?: HttpContext): Observable<Envelop> {
    return this.http.get<Envelop>(this.url, { context });
  }

  getById(
    id: string | number,
    headers: QueryHeaders = {},
    context?: HttpContext
  ): Observable<Type> {
    return this.http
      .get<Type>(`${this.url}/${id}`, { ...getHeaders(headers), context })
      .pipe(map((dto) => this.mapDto(dto)));
  }

  getWithHref(href: string): Observable<Envelop> {
    return this.http.get<Envelop>(href ? `${this.url}?${href}` : this.url);
  }

  getWithQuery(params: Partial<QueryParams>): Observable<Envelop>;
  getWithQuery(options: GetWithQueryOptions): Observable<Envelop>;
  getWithQuery(paramsOrOptions: Partial<QueryParams> | GetWithQueryOptions): Observable<Envelop> {
    const { params, headers, context, withAccountId } = extractGetOptions(paramsOrOptions);
    const url = withAccountId
      ? getUrlWithAccountId({ apiUrl: this.apiUrl, accountId: withAccountId, uri: this.uri })
      : this.url;
    return this.http
      .get<Envelop>(url, { params, ...getHeaders(headers), context })
      .pipe(map((envelope) => this.mapEnvelop(envelope)));
  }

  update(
    id: string,
    body: (Partial<Patch> | Patch) & { _version?: string },
    context?: HttpContext
  ): Observable<Type> {
    return this.http
      .patch<Type>(`${this.url}/${id}`, body, { context })
      .pipe(map((dto) => this.mapDto(dto)));
  }

  /**
   * Maps all items in a list envelope through {@link mapDto}.
   * Override if your envelope has a non-standard shape.
   */
  protected mapEnvelop(envelope: Envelop): Envelop {
    const env = envelope as unknown as { _embedded?: { results?: Type[] } };
    if (env._embedded?.results) {
      return {
        ...envelope,
        _embedded: {
          ...env._embedded,
          results: env._embedded.results.map((dto) => this.mapDto(dto)),
        },
      };
    }
    return envelope;
  }
}

const getHeaders = ({ maxRetry }: QueryHeaders): { headers?: HttpHeaders } => ({
  ...(maxRetry ? { headers: new HttpHeaders({ maxRetry: `${maxRetry}` }) } : {}),
});

const isParams = (
  paramsOrOptions: Partial<QueryParams> | GetWithQueryOptions
): paramsOrOptions is Partial<QueryParams> =>
  (paramsOrOptions as GetWithQueryOptions)?.params === undefined;

export const extractGetOptions = (paramsOrOptions: Partial<QueryParams> | GetWithQueryOptions) => {
  const params = isParams(paramsOrOptions) ? paramsOrOptions : paramsOrOptions.params;
  const {
    headers = {},
    context = undefined,
    withAccountId = '',
  } = isParams(paramsOrOptions)
    ? { headers: {}, context: undefined, withAccountId: '' }
    : {
        headers: paramsOrOptions.headers ?? {},
        context: paramsOrOptions.context,
        withAccountId: paramsOrOptions.withAccountId,
      };

  return { params, headers, context, withAccountId };
};

const getUrlWithAccountId = ({
  apiUrl,
  accountId,
  uri,
}: {
  apiUrl: string;
  accountId: string;
  uri: string;
}): string => `${apiUrl}/account/${accountId}/${uri}`;

/** Runtime guard — checks if a value has the core Revision audit fields.
 * Only `createdAt` and `createdBy` are checked since `updatedAt`/`updatedBy`
 * may be absent from the raw response when null (API omits the key). */
const isRevision = (value: unknown): value is Revision =>
  typeof value === 'object' && value !== null && 'createdAt' in value && 'createdBy' in value;
